import { CircleCheck, CircleX, Info, X } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useSyncExternalStore } from 'react'

import { cn } from '@/lib/cn'

/**
 * Feedback de sucesso em um padrão só: um aviso curto no rodapé, que some
 * sozinho. Erro de formulário continua inline (perto do campo); o toast de
 * erro é para ações sem formulário (remover, gerar, revogar).
 */
export type TipoToast = 'sucesso' | 'erro' | 'info'

export interface Toast {
  id: number
  tipo: TipoToast
  texto: string
}

type Ouvinte = () => void

let toasts: Toast[] = []
let proximoId = 1
const ouvintes = new Set<Ouvinte>()

function assinar(ouvinte: Ouvinte) {
  ouvintes.add(ouvinte)
  return () => {
    ouvintes.delete(ouvinte)
  }
}
const lerToasts = () => toasts
const DURACAO_MS: Record<TipoToast, number> = { sucesso: 3500, info: 5000, erro: 7000 }

function emitir() {
  for (const o of ouvintes) o()
}

function remover(id: number) {
  toasts = toasts.filter((t) => t.id !== id)
  emitir()
}

function mostrar(tipo: TipoToast, texto: string) {
  const id = proximoId++
  // Mesmo texto repetido (dois cliques) não empilha.
  toasts = [...toasts.filter((t) => t.texto !== texto), { id, tipo, texto }].slice(-3)
  emitir()
  setTimeout(() => remover(id), DURACAO_MS[tipo])
  return id
}

export const toast = {
  sucesso: (texto: string) => mostrar('sucesso', texto),
  erro: (texto: string) => mostrar('erro', texto),
  info: (texto: string) => mostrar('info', texto),
  /** Só para testes. */
  limpar: () => {
    toasts = []
    emitir()
  },
}

const ESTILO: Record<TipoToast, { classe: string; Icone: typeof Info; rotulo: string }> = {
  sucesso: { classe: 'border-verde/40 text-verde', Icone: CircleCheck, rotulo: 'Sucesso' },
  erro: { classe: 'border-vermelho/40 text-vermelho', Icone: CircleX, rotulo: 'Erro' },
  info: { classe: 'border-brand/40 text-brand', Icone: Info, rotulo: 'Aviso' },
}

/** Monte uma vez, na raiz. */
export function Toasts() {
  const lista = useSyncExternalStore(assinar, lerToasts, lerToasts)

  return (
    <div
      aria-live="polite"
      aria-relevant="additions"
      className="nao-imprimir pointer-events-none fixed inset-x-0 bottom-6 z-[60] flex flex-col items-center gap-2 px-4"
    >
      <AnimatePresence>
        {lista.map((t) => {
          const { classe, Icone, rotulo } = ESTILO[t.tipo]
          return (
            <motion.div
              key={t.id}
              role={t.tipo === 'erro' ? 'alert' : 'status'}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.18 }}
              className={cn(
                'pointer-events-auto flex max-w-lg items-center gap-3 rounded-2xl border bg-elevated py-3 pr-2 pl-4 text-sm font-medium text-fg shadow-card',
                classe,
              )}
            >
              <Icone className="size-5 shrink-0" aria-hidden />
              <span className="sr-only">{rotulo}: </span>
              <span className="text-fg">{t.texto}</span>
              <button
                type="button"
                aria-label="Fechar aviso"
                onClick={() => remover(t.id)}
                className="ml-1 grid size-9 shrink-0 place-items-center rounded-xl text-fg-muted hover:bg-surface hover:text-fg"
              >
                <X className="size-4" aria-hidden />
              </button>
            </motion.div>
          )
        })}
      </AnimatePresence>
    </div>
  )
}
